import { RELOAD_PAGE_AT_MOST_MS } from "./constants";
import {
  buildChunkDiagnostics,
  ChunkRecoveryError,
  type ChunkFailureContext,
} from "./diagnostics";
import { getLastReloadAt, setLastReloadAt } from "./reloadGuard";

/**
 * Reload the page, unless we already reloaded in the last 10s.
 * Returns true if it actually reloaded. Sharing one guard means the inline
 * and global callers can't ping-pong each other into a loop.
 * (`reload` and `now` are swappable for tests.)
 */
export const reloadPageOnChunkFailure = (
  reload: () => void = () => window.location.reload(), // pass this as a param for injection by tests
  now: number = Date.now(),
): boolean => {
  const last = getLastReloadAt();
  const reloadedRecently = last !== 0 && now - last < RELOAD_PAGE_AT_MOST_MS;
  if (reloadedRecently) return false;

  setLastReloadAt(now);
  reload();
  return true;
};

/**
 * Use inside a `catch` (e.g. Inertia's page loader). If we reload, this hands
 * back a promise that never finishes, so the caller just stops instead of
 * touching the broken chunk. If the guard blocked the reload (we already tried
 * and it's still broken), it rejects instead — better to show an error than
 * spin forever.
 *
 * Pass `context` so the rejection explains why recovery failed. (`reload` is a
 * param for injection by tests.)
 */
export const reloadAndHalt = (
  context?: ChunkFailureContext,
  reload?: () => void,
): Promise<never> => {
  if (reloadPageOnChunkFailure(reload)) {
    return new Promise<never>(() => undefined);
  }
  return Promise.reject(
    new ChunkRecoveryError(
      context ? buildChunkDiagnostics(context) : null,
      context?.cause,
    ),
  );
};
